import { API_BASE_URL } from "@/config";
import { canonicalEventId } from "@/lib/eventIdentity";
import type { Log2simSceneGeometry } from "@/lib/log2simScene";
import { authHeaders } from "@/utils/authHeaders";
import { apiClient } from "./apiClient";

/**
 * Client for the log2sim event-simulation endpoints. Mirrors the Runway
 * generation API (services/batchApi.ts) shape-for-shape so callers can share
 * polling and rendering patterns.
 */

const safeErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
  } catch {
    // Ignore response parse failures.
  }
  return `Request failed with status ${response.status}`;
};

export type Log2simEditType = "remove" | "shift" | "retime";
export type Log2simGenerationStatus =
  | "queued"
  | "running"
  | "succeeded"
  | "failed";

export interface Log2simEditSpec {
  /** The actor this edit applies to. One edit per actor. */
  track_id: string;
  type: Log2simEditType;
  /** shift only. Metres, roughly [-6, 6]. Negative = left. */
  offset_m?: number;
  /** shift only. Seconds, roughly [0, 10]. */
  ramp_s?: number;
  /** retime only. Multiplier of clip speed. */
  speed_factor?: number;
}

export interface Log2simEventGeneration {
  generation_id: string;
  status: Log2simGenerationStatus | string;
  stage?: string;
  progress?: number | null;
  /** Frames rendered so far in the current pass (0 during startup). */
  frame?: number | null;
  /** Estimated total frames for the pass; null while unknown (startup). */
  total_frames?: number | null;
  batch_id?: string;
  video_id?: string;
  analysis_id?: string;
  event_id?: string;
  event_start_s?: number | null;
  event_end_s?: number | null;
  edits?: Log2simEditSpec[] | null;
  reactive?: boolean | null;
  resolution_ratio?: number | null;
  output_url?: string | null;
  /** Derived Workbench view id created on success (display-only "Render"). */
  derived_video_id?: string | null;
  download_filename?: string | null;
  error?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface Log2simAvailableResponse {
  video_id: string;
  available: boolean;
}

export interface FetchLog2simSceneParams {
  batchId: string;
  videoId: string;
  shareToken?: string | null;
}

/**
 * Cheap "can this video be simulated?" check, live from the render service (it
 * never downloads the scene). Returns the boolean on success; a 404 (video not
 * in batch) => false; a gate-down (5xx) throws, so callers can distinguish it
 * from a genuine `available: false`.
 */
export async function fetchLog2simAvailable(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<boolean> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/log2sim/available${query}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (response.status === 404) return false;
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  const data = (await response.json()) as Log2simAvailableResponse;
  return !!data.available;
}

export interface Log2simActor {
  track_id: string;
  label?: string;
  path_length_m?: number;
  visible_s?: number;
  visible_frames?: [number, number];
  closest_approach_m?: number;
}

export interface Log2simActorsResponse {
  video_id: string;
  actors: Log2simActor[];
}

/**
 * Editable actors for a video, live from the render service (via our backend).
 * This is the source of truth for which actors an edit can target — the
 * preprocessed scene JSON can list actors the render box rejects. Throws on a
 * service error so the dialog can surface it.
 */
export async function fetchLog2simActors(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<Log2simActorsResponse> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/log2sim/actors${query}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simActorsResponse;
}

/**
 * Full 3D scene geometry (+ per-actor `editable` flag) for the simulation
 * dialog, live from the render service. Replaces the preprocessed scene JSON.
 * Returns null when the render service has no scene for this video.
 */
export async function fetchLog2simSceneGeometry(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<Log2simSceneGeometry | null> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/log2sim/scene-geometry${query}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simSceneGeometry;
}

export type Log2simPrepareState = "ready" | "queued" | "downloading" | "error";

export interface Log2simPrepareStatus {
  video_id: string;
  clip_id?: string;
  state: Log2simPrepareState | string;
  /** 0..1 fraction of the ~2 GB scene download, when the box reports it. */
  progress?: number | null;
  /** Short human status, e.g. "downloading scene (63%)". */
  stage?: string | null;
  error?: string | null;
}

/**
 * Kick (or join) the async scene download on the render service and return the
 * current status immediately — never blocks. Idempotent (one download per clip).
 */
export async function startLog2simPrepare(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<Log2simPrepareStatus> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/log2sim/prepare${query}`;
  const response = await apiClient.post(url, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simPrepareStatus;
}

/** Poll the async scene-download status (does NOT trigger a download). */
export async function fetchLog2simPrepareStatus(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<Log2simPrepareStatus> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/log2sim/prepare${query}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simPrepareStatus;
}

/**
 * Whether a video can be simulated, from the render service's cheap /available
 * gate (never downloads the scene). Deduped across concurrent callers and
 * cached for the lifetime of the tab, so re-renders of the batch viewer (one
 * EventsList per video, remounting on tab switches) don't re-probe repeatedly.
 *
 * The cache stores the pending promise so simultaneous first-time calls from N
 * EventsList instances collapse into a single request. If the gate itself is
 * DOWN (5xx / network) we optimistically resolve `true` — better to show the
 * button and let the dialog surface a real error than to hide every button
 * when only the gate is unavailable.
 */
const _sceneProbeCache = new Map<string, Promise<boolean>>();

export function probeLog2simSceneAvailable(
  params: FetchLog2simSceneParams,
  token: string | null,
): Promise<boolean> {
  const key = `${params.batchId}:${params.videoId}:${params.shareToken ?? ""}`;
  const cached = _sceneProbeCache.get(key);
  if (cached) return cached;
  const pending = fetchLog2simAvailable(params, token).catch(() => true);
  _sceneProbeCache.set(key, pending);
  return pending;
}

export interface CreateLog2simGenerationParams {
  batchId: string;
  videoId: string;
  analysisId: string;
  eventId: string;
  /** One edit per altered actor (each carries its own track_id). */
  edits: Log2simEditSpec[];
  /** Other agents (IDM) react to all edits at once. */
  reactive?: boolean;
  resolutionRatio?: number;
  shareToken?: string | null;
}

export async function createLog2simGeneration(
  params: CreateLog2simGenerationParams,
  token: string | null,
): Promise<Log2simEventGeneration> {
  const query = params.shareToken
    ? `?share_token=${encodeURIComponent(params.shareToken)}`
    : "";
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/events/${encodeURIComponent(
    canonicalEventId(params.eventId),
  )}/log2sim-generations${query}`;
  const response = await apiClient.post(url, {
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({
      analysis_id: params.analysisId,
      edits: params.edits,
      reactive: !!params.reactive,
      ...(params.resolutionRatio !== undefined
        ? { resolution_ratio: params.resolutionRatio }
        : {}),
    }),
  });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simEventGeneration;
}

export interface ListLog2simGenerationsParams {
  batchId: string;
  videoId: string;
  analysisId: string;
  eventId: string;
  shareToken?: string | null;
  limit?: number;
}

export interface ListLog2simGenerationsResponse {
  event_id: string;
  generations: Log2simEventGeneration[];
}

export async function listLog2simGenerations(
  params: ListLog2simGenerationsParams,
  token: string | null,
): Promise<ListLog2simGenerationsResponse> {
  const queryParams = new URLSearchParams({ analysis_id: params.analysisId });
  if (params.shareToken) queryParams.set("share_token", params.shareToken);
  if (params.limit) queryParams.set("limit", String(params.limit));
  const url = `${API_BASE_URL}/batch/${params.batchId}/videos/${params.videoId}/events/${encodeURIComponent(
    canonicalEventId(params.eventId),
  )}/log2sim-generations?${queryParams.toString()}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as ListLog2simGenerationsResponse;
}

export async function fetchLog2simGeneration(
  generationId: string,
  token: string | null,
  shareToken?: string | null,
): Promise<Log2simEventGeneration> {
  const query = shareToken
    ? `?share_token=${encodeURIComponent(shareToken)}`
    : "";
  const url = `${API_BASE_URL}/log2sim-generations/${encodeURIComponent(generationId)}${query}`;
  const response = await apiClient.get(url, { headers: authHeaders(token) });
  if (!response.ok) throw new Error(await safeErrorMessage(response));
  return (await response.json()) as Log2simEventGeneration;
}
